// react
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';

// components
import { Button, Dialog, Tabs, Title } from '@/components/atoms';

// organisms
import { ChangePasswordForm } from '@/components/organisms';

// models
import { Account } from '@karehero/models';

/**
 * Settings renders the account settings page.
 */
export const Settings = ({
  account,
  onConfirm,
  appVersion,
  apiVersion,
}: SettingsProps) => {
  // state
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [searchParams] = useSearchParams();

  // methods
  const handleConfirm = useCallback(
    async (password: string, confirmPassword: string) => {
      try {
        await onConfirm?.(password, confirmPassword);
        setIsChangePasswordOpen(false);
      } catch (e) {
        console.error(e);
      }
    },
    [onConfirm],
  );

  return (
    <Wrapper>
      <Title>Settings</Title>
      <Tabs
        defaultValue={searchParams.get('tab') || '1'}
        tabs={[
          {
            id: '1',
            label: `My account`,
            content: (
              <AccountWrapper>
                <Field>
                  <SubTitle>Name</SubTitle>
                  <HelperText>
                    {`${account.firstName} ${account?.lastName || ''}`}
                  </HelperText>
                </Field>

                <Field>
                  <SubTitle>Email address</SubTitle>
                  <HelperText>{account.kindeEmail}</HelperText>
                </Field>

                {account.partnerCode && (
                  <Field>
                    <SubTitle>Partner Code</SubTitle>
                    <HelperText>{account.partnerCode}</HelperText>
                  </Field>
                )}

                {account.referLtcCode && (
                  <Field>
                    <SubTitle>LTC License</SubTitle>
                    <HelperText>{account.referLtcCode}</HelperText>
                  </Field>
                )}

                {onConfirm && (
                  <Field>
                    <SubTitle>Password</SubTitle>
                    <Button
                      ariaLabel='change password'
                      variant='secondary'
                      onPress={() => setIsChangePasswordOpen(true)}
                    >
                      Change password
                    </Button>
                    <Dialog
                      title='Change Password'
                      isCloseButton
                      isOpen={isChangePasswordOpen}
                      setIsOpen={setIsChangePasswordOpen}
                    >
                      <ChangePasswordForm onConfirm={handleConfirm} />
                    </Dialog>
                  </Field>
                )}

                <Field>
                  <SubTitle>Delete account</SubTitle>
                  <HelperText>
                    If you wish to delete your account, please contact &nbsp;
                    <Button
                      variant='link'
                      size='md'
                      isInline
                      ariaLabel='here'
                      to={`mailto://${process.env.REACT_APP_SUPPORT_EMAIL}`}
                    >
                      {process.env.REACT_APP_SUPPORT_EMAIL}
                    </Button>
                  </HelperText>
                </Field>
              </AccountWrapper>
            ),
          },
          {
            id: '2',
            label: 'App info',
            content: (
              <AccountWrapper>
                <Field>
                  <SubTitle>App version</SubTitle>
                  <HelperText>{appVersion}</HelperText>
                </Field>

                <Field>
                  <SubTitle>API version</SubTitle>
                  <HelperText>{apiVersion}</HelperText>
                </Field>
              </AccountWrapper>
            ),
          },
        ]}
      />
    </Wrapper>
  );
};

// styles
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: ${(props) => props.theme.spacing[16]};
`;

const AccountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[48]};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[8]};
  align-items: flex-start;
`;

const SubTitle = styled.div`
  font: ${(props) => props.theme.typography.body.md.semibold};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const HelperText = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[80]};
`;

// types
interface SettingsProps {
  onConfirm?: (password: string, confirmPassword: string) => Promise<void>;
  account: Account;
  appVersion: string;
  apiVersion: string;
}

Settings.displayName = 'Settings';

export default Settings;
